import { showErrorLayout } from 'widgets/toolbox/util';
import { submitFormJsonWithAbort } from 'widgets/toolbox/ajax';
import { TProductVariantSelectInstance } from 'widgets/product/ProductVariantSelect';
import type { TMobileProductVariantSelect } from './MobileProductVariantSelect';
import { TProduct as TBaseProduct, TAvailabilityModel } from 'types/models/product';
import { RefElement } from 'widgets/toolbox/RefElement';
import { isSmallView } from 'widgets/toolbox/viewtype';

import type { TSwiper } from 'widgets/global/CarouselSwiper';

type TSwiperElement = HTMLElement & {
    swiper?: TSwiper & {
        params?: {
            slidesPerView?: string | number;
        };
        navigation?: {
            update: () => void;
        };
    };
};

const SERVICE_CHANGE_EVENT = 'consentServiceChange';
const SERVICE_OPTIMIZELY = 'Optimizely';
const OPTIMIZELY_ELEVATION_TREATMENT = 'treatment_1';

export type TProduct = TBaseProduct & {
    price: TBaseProduct['price'] & { html: string };
    stock: null | number;
    availability: TAvailabilityModel;
    selectedAttributes: Array<{
        displayName: string;
        id: string;
        value: string;
    }>;
    pdpElevation: {
        showElevationGallery: boolean;
        showElevationDetails: boolean;
        hasElevationImages: boolean;
        eligibleForElevationGallery: boolean;
    };
};

export type TUpdateProductResponse = {
    product: TProduct;
    analyticsPageDataPatch: Record<string, unknown>;
};

/**
 * @description CoreProductVariationChangeMixin implementation
 * @param ProductVariationChangeMixin Base widget for extending
 * @returns widget which triggers Bonus Product functionality
 */
function CoreProductVariationChangeMixinClassCreator(
    ProductVariationChangeMixin: import('widgets/product/ProductVariationChangeMixin').TProductVariationChangeMixin
) {
    /**
     * @category widgets
     * @subcategory product
     * @class CoreProductVariationChangeMixin
     * @augments ProductVariationChangeMixin
     * @classdesc Represents CoreProductVariationChangeMixin component
     */
    class CoreProductVariationChangeMixin extends ProductVariationChangeMixin {
        private readonly PDP_THUMBNAIL_TAG = 'pdp_thumbnail_tag';

        private readonly SWATCH_IMAGERY = 'pdp_thumbnail_swatch_imagery';

        private optimizelyDecisions: Record<string, string> = {};

        private elevationGalleryRequestPending = false;

        prefs() {
            return {
                optimizelyFeatureFlagKeys: '',
                elevationOptimizelyFlagKey: '',
                elevationVariationUrl: '',
                ...super.prefs()
            };
        }

        init() {
            super.init();

            this.handlePreselectedWidth();

            this.eventBus().on('product.variant.change', 'changeAttribute');
            this.eventBus().on('product.variant.changed', 'applyExperimentLayoutPdp');
            this.eventBus().on(SERVICE_CHANGE_EVENT, 'processElevationConsentChange');
            this.initOptimizelyPdp();
        }

        /**
         * @description Initializes Optimizely experiments setup on component
         * via optimizelyFeatureFlagKey.
         */
        async initOptimizelyPdp() {
            const isConsentGiven = this.callIfExists('getConsentStatus') as boolean;

            if (!isConsentGiven) {
                return;
            }

            const optimizelyFeatureFlagKeys = this.prefs().optimizelyFeatureFlagKeys;
            const variationsContent = this.getVariationsContentElement();

            if (!optimizelyFeatureFlagKeys) {
                variationsContent?.show();

                return;
            }

            const optimizelyFeatureFlagKeysArr = optimizelyFeatureFlagKeys
                .split(',')
                .map(optimizelyFeatureFlagKey => optimizelyFeatureFlagKey.trim())
                .filter(Boolean);

            if (this.prefs().elevationOptimizelyFlagKey && !(await this.waitForOptimizelySDK())) {
                return;
            }

            await Promise.all(
                optimizelyFeatureFlagKeysArr.map(async optimizelyFeatureFlagKey => {
                    const decision = (await this.callIfExists('decideFeatureFlagVariation', optimizelyFeatureFlagKey)) as string;

                    this.optimizelyDecisions[optimizelyFeatureFlagKey] = decision;
                })
            );

            this.applyExperimentLayoutPdp();
            variationsContent?.show();
        }

        /**
         * Re-runs PDP decisions when Optimizely consent is granted without a page reload.
         *
         * @param type - Consent service name.
         * @param accepted - Whether consent was granted.
         */
        processElevationConsentChange(type: string, accepted: boolean): void {
            if (type === SERVICE_OPTIMIZELY && accepted) {
                this.initOptimizelyPdp().catch(() => {
                    // decideFeatureFlagVariation logs its own failures
                });
            }
        }

        /**
         * Waits for OneTrust to activate the consent-gated Optimizely SDK script.
         *
         * @returns Whether the SDK became available before the timeout.
         */
        private waitForOptimizelySDK(): Promise<boolean> {
            const POLL_INTERVAL = 100;
            const TIMEOUT = 10000;

            if (window.optimizelySdk) {
                return Promise.resolve(true);
            }

            return new Promise(resolve => {
                const startedAt = Date.now();
                const interval = window.setInterval(() => {
                    if (window.optimizelySdk || Date.now() - startedAt >= TIMEOUT) {
                        window.clearInterval(interval);
                        resolve(!!window.optimizelySdk);
                    }
                }, POLL_INTERVAL);
            });
        }

        /**
         * Applies the configured Optimizely treatment to the initial server-rendered gallery.
         *
         * @param decision - Optimizely variation decision.
         */
        private applyElevationGalleryLayout(decision: string): void {
            const gallery = document.querySelector<HTMLElement>('.js-pdp-main [data-id="productImages"]');
            const elevationVariationUrl = this.prefs().elevationVariationUrl;

            if (
                decision !== OPTIMIZELY_ELEVATION_TREATMENT ||
                !elevationVariationUrl ||
                gallery?.dataset.elevationGallery === 'true' ||
                this.elevationGalleryRequestPending
            ) {
                return;
            }

            this.elevationGalleryRequestPending = true;

            const requestObject = submitFormJsonWithAbort<TUpdateProductResponse>(elevationVariationUrl, { isColorChange: 'true' }, 'GET', true);

            requestObject.promise
                .then(response => {
                    if (!response?.success || !response.product) {
                        return;
                    }

                    const currentProductId = this.prefs().currentProductId;

                    if (currentProductId && response.product.id !== currentProductId) {
                        return;
                    }

                    this.applyElevationDecisionToProduct(response.product);
                    this.renderImages(response.product);
                })
                .catch(showErrorLayout)
                .finally(() => {
                    this.elevationGalleryRequestPending = false;
                });
        }

        /**
         * Combines server-side product eligibility with the browser Optimizely assignment.
         *
         * @param product - Product returned by Product-Variation.
         */
        private applyElevationDecisionToProduct(product: TProduct): void {
            const elevation = product?.pdpElevation;
            const elevationFlagKey = this.prefs().elevationOptimizelyFlagKey;

            if (!elevation || !elevationFlagKey || typeof elevation.eligibleForElevationGallery !== 'boolean') {
                return;
            }

            elevation.showElevationGallery =
                elevation.eligibleForElevationGallery &&
                elevation.hasElevationImages !== false &&
                this.optimizelyDecisions[elevationFlagKey] === OPTIMIZELY_ELEVATION_TREATMENT;
        }

        /**
         * @description Applies experiment layout for flag decision.
         */
        applyExperimentLayoutPdp() {
            Object.keys(this.optimizelyDecisions).forEach(optimizelyFeatureFlagKey => {
                const decision = this.optimizelyDecisions[optimizelyFeatureFlagKey];

                if (optimizelyFeatureFlagKey === this.prefs().elevationOptimizelyFlagKey) {
                    this.applyElevationGalleryLayout(decision);

                    return;
                }

                if (decision === 'off') {
                    return;
                }

                if (optimizelyFeatureFlagKey.includes(this.PDP_THUMBNAIL_TAG)) {
                    this.applyPdpThumbnailTagLayout(decision);
                }

                if (optimizelyFeatureFlagKey.includes(this.SWATCH_IMAGERY)) {
                    this.applyImageSwatchLayout(decision);
                }
            });
        }

        /**
         * @description Applies image swatch experiment layout for PDP color swatches.
         * @param decision Optimizely variation decision
         */
        applyImageSwatchLayout(decision) {
            if (decision === 'treatment_1') {
                const variationsContent = this.getVariationsContentElement();

                if (variationsContent) {
                    this.applyImageSwatchStyles(variationsContent);
                    this.updateSwiperDimensions(variationsContent);
                }
            }
        }

        /**
         * @description Updates Swiper dimensions after image swatch styles change slide sizing.
         * @param variationsContent Color swatches content ref
         */
        updateSwiperDimensions(variationsContent: RefElement<HTMLElement>): void {
            const swiperElement = variationsContent?.get()?.closest<TSwiperElement>('.b-carousel_swiper');

            if (!swiperElement) {
                return;
            }

            const updateSwiper = (): boolean => {
                if (!swiperElement.swiper?.params) {
                    return false;
                }

                swiperElement.swiper.params.slidesPerView = 'auto';
                swiperElement.swiper.update();
                swiperElement.swiper.navigation?.update();

                return true;
            };

            if (updateSwiper()) {
                return;
            }

            const observer = new MutationObserver(() => {
                if (updateSwiper()) {
                    observer.disconnect();
                }
            });

            observer.observe(swiperElement, {
                attributes: true,
                attributeFilter: ['class']
            });
        }

        /**
         * @description Applies image URLs and styling class to color swatches.
         * @param variationsContent Color swatches content ref
         */
        applyImageSwatchStyles(variationsContent: RefElement<HTMLElement>): void {
            variationsContent
                ?.get()
                ?.querySelectorAll('.b-variation_swatch-color_value')
                .forEach(span => {
                    const colorValue = span as HTMLElement;
                    const swatchImageUrl = colorValue.dataset.imageSwatchUrl;

                    if (swatchImageUrl) {
                        colorValue.style.backgroundImage = `url('${swatchImageUrl}')`;
                    }
                });

            variationsContent?.addClass('m-image-swatch');
        }

        /**
         * @description Gets current PDP color swatches content element for active viewport.
         * @returns Color swatches content ref if available
         */
        getVariationsContentElement(): RefElement<HTMLElement> | undefined {
            const MobileProductVariantSelect = this.getConstructor('mobileProductVariantSelect') as TMobileProductVariantSelect;
            const mobileProductVariationSelect = this.items?.find(item => item instanceof MobileProductVariantSelect);
            const productVariationColor =
                isSmallView() || mobileProductVariationSelect?.config?.quickView
                    ? mobileProductVariationSelect?.items[0]
                    : this.items?.find(item => item.id === 'attr-color-bottom');
            const variationsContent = productVariationColor?.ref('variationsContent');

            return variationsContent;
        }

        applyPdpThumbnailTagLayout(decision) {
            const TREATMENTS = {
                treatment_1: 'sale',
                treatment_2: 'final',
                treatment_3: 'off'
            };

            if (!Object.keys(TREATMENTS).includes(decision)) {
                return;
            }

            const container = (document.getElementById('attr-color-bottom') as HTMLElement) || document;

            container.querySelectorAll('.b-variation_swatch-badges').forEach(el => {
                const badgeElement = el as HTMLElement;
                const badges = badgeElement.dataset.attrBadges?.split(',') || [];
                const newBadge = badges.find(badge => badge === 'new');
                const treatmentBadge = badges.find(badge => badge.toLowerCase().includes(TREATMENTS[decision]));
                const badge = treatmentBadge || newBadge || '';

                if (badge) {
                    badgeElement.innerText = badge;
                    badgeElement.classList.add('m-visible');
                }
            });
        }

        /**
         * @description Render product attributes
         * @param product Product object
         */
        renderVariationAttributes(product: TProduct): void {
            if (product.variationAttributes?.length) {
                product.variationAttributes.forEach(variationAttribute => {
                    variationAttribute.values = variationAttribute.values.map(value => {
                        return {
                            ...value,
                            selected: value.selected,
                            disabled: !value.selectable
                        };
                    });

                    if (variationAttribute.attributeId === 'color') {
                        Promise.all([
                            this.renderAttribute(
                                'attr-' + variationAttribute.attributeId + '-top',
                                variationAttribute,
                                product.selectedAttributes,
                                product.id,
                                product.gtmInfo
                            ),
                            this.renderAttribute(
                                'attr-' + variationAttribute.attributeId + '-bottom',
                                variationAttribute,
                                product.selectedAttributes,
                                product.id,
                                product.gtmInfo
                            )
                        ]).then(() => {
                            this.eventBus().emit('product.variant.changed');
                        });
                    } else {
                        this.renderAttribute(
                            'attr-' + variationAttribute.attributeId,
                            variationAttribute,
                            product.selectedAttributes,
                            product.id,
                            product.gtmInfo
                        );
                    }
                });
            }
        }

        renderAttribute(
            id: string,
            variationAttribute,
            selectedAttributes: TProduct['selectedAttributes'],
            productId: string,
            gtmInfo: TProduct['gtmInfo']
        ): Promise<void> {
            const childComponent = this.getByIdDeep(id, attribute => {
                return attribute.render(
                    'template',
                    {
                        selectedAttributes,
                        attr: variationAttribute,
                        productId,
                        gtmInfo
                    },
                    attribute.ref('container')
                );
            });

            return childComponent !== undefined ? Promise.resolve(childComponent) : Promise.resolve();
        }

        /**
         * @description trigger change from different widgets (swatch, select)
         * Saves `selectWidget.id` into a property for further global notifications etc.
         * Later, when triggering event, `selectWidget.id` will be analyzed in order to send with event correct data.
         * @param selectWidget widget
         * @returns Promise object represents server response with attribute changing result
         */
        changeAttribute(selectWidget: TProductVariantSelectInstance) {
            this.changeAttributeID = selectWidget.id;
            const selected = selectWidget.getSelectedVariations();
            let requestData;
            let isColorChange = false;

            if (!selected || selected.data('attrIsSelected')) {
                return Promise.resolve(null);
            }

            if (selectWidget.id?.startsWith('attr-color')) {
                isColorChange = true;
                requestData = {
                    isColorChange: isColorChange
                };
            }

            const requestObject = submitFormJsonWithAbort<TUpdateProductResponse>(this.getAttributeChangingURL(selected), requestData, 'GET', true);

            this.changeAttributeRequests.push(requestObject);

            selectWidget.ref('content').toggleClass(this.prefs().classesLoading, true).attr('aria-busy', 'true');

            return requestObject.promise
                .then((response: Awaited<typeof requestObject.promise>) => {
                    if (!response) {
                        return null; // Aborted request
                    }

                    if (!response.success) {
                        this.showCartMessage(response.errorMessage, true);

                        return null;
                    }

                    this.afterAttributeChange(response);

                    if (isColorChange) {
                        this.eventBus().emit('product.color.change', { id: response.product?.id });
                        this.handlePreselectedWidth();
                    }

                    return response;
                })
                .catch(error => {
                    showErrorLayout(error);

                    return null;
                })
                .finally(() => {
                    const index = this.changeAttributeRequests.indexOf(requestObject);

                    selectWidget.ref('content').toggleClass(this.prefs().classesLoading, false);

                    if (index > -1) {
                        this.changeAttributeRequests.splice(index, 1);
                    }
                });
        }

        /**
         * @description Update canonical URL
         * @param newrelativeUrl New canonical URL
         */
        updateCanonicalUrl(newrelativeUrl) {
            const canonicalLink = document.querySelector('link[rel="canonical"]');

            if (canonicalLink) {
                const domain = window.location.origin;
                const newCanonicalUrl = domain + newrelativeUrl;

                canonicalLink.setAttribute('href', newCanonicalUrl);
            }
        }

        /**
         * @description Get product attribute updating URL
         * @param selectedAttribute Selected attribute element
         */
        getAttributeChangingURL(selectedAttribute: RefElement): string {
            let URL = super.getAttributeChangingURL(selectedAttribute);

            URL += (URL.includes('?') ? '&' : '?') + 'quantity=' + (this.prefs().selectedQuantity || '1');

            return URL;
        }

        /**
         * @description After change attribute handler
         * @param response response object
         * @param response.product - product
         */
        afterAttributeChange(response: TUpdateProductResponse): void {
            this.applyElevationDecisionToProduct(response?.product);
            super.afterAttributeChange(response);

            if (response && response.analyticsPageDataPatch && !this.prefs().disableHistory) {
                this.eventBus().emit(
                    'gtm.update',
                    {
                        analyticsPageData: response.analyticsPageDataPatch
                    },
                    {
                        extend: true,
                        reportPageMeta: true
                    }
                );
            }
        }

        /**
         * @description Update History State
         * @param product product object
         */
        updateHistoryState(product: TProduct): void {
            if (product.selectedProductUrl) {
                window.history.replaceState(undefined, '', product.selectedProductUrl + window.location.search);
                this.updateCanonicalUrl(product.selectedProductUrl);

                this.eventBus().emit('updatebreadcrumbs', {
                    newUrl: product.selectedProductUrl,
                    productName: product.productName
                });
            }
        }

        /**
         * @description Maps the changed attribute widget id (e.g. "attr-color",
         * "attr-size", "attr-width") to an analytics cause used to distinguish
         * a color switch (full product view) from a size/width selection
         * (product option selection).
         * @returns analytics cause or undefined if the attribute is not tracked
         */
        getAttributeChangeCause(): string | undefined {
            if (!this.changeAttributeID) {
                return undefined;
            }

            if (this.changeAttributeID.startsWith('attr-color')) {
                return 'color_switch';
            }

            if (this.changeAttributeID.startsWith('attr-size')) {
                return 'size_selection';
            }

            if (this.changeAttributeID.startsWith('attr-width')) {
                return 'width_selection';
            }

            return undefined;
        }

        /**
         * @description Triggers global event after `afterAttributeChange` method executed.
         * Puts localized global alert event description
         * Replaced because: it no longer needs to show an alert
         * @param response response object
         */
        triggerChangeAttributeEvent(response: TUpdateProductResponse): void {
            if (!this.changeAttributeID) {
                return;
            }

            const cause = this.getAttributeChangeCause();

            this.changeAttributeID = undefined;

            this.afterUpdateProduct(response, cause);
        }

        /**
         * @description Handle preselected width value
         * Adds active 'm-active_variation' class to the main element if width is preselected and product is not a set item
         */
        handlePreselectedWidth(): void {
            if (!this.config.productSetItem && this.config.attributeSelections && typeof this.config.attributeSelections === 'object') {
                Object.entries(this.config.attributeSelections).forEach(([attribute, isSelected]) => {
                    if (attribute === 'width') {
                        this.ref('html').toggleClass('m-active_variation', isSelected === true);
                    }
                });
            }
        }
    }

    return CoreProductVariationChangeMixin;
}

export type TCoreProductVariationChangeMixin = ReturnType<typeof CoreProductVariationChangeMixinClassCreator>;

export type TCoreProductVariationChangeMixinInstance = InstanceType<TCoreProductVariationChangeMixin>;

export default CoreProductVariationChangeMixinClassCreator;
